import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from './Layout';
import './Products.css';

const Products = () => {
  const [activeTab, setActiveTab] = useState('weight-loss');
  const [products, setProducts] = useState([]);
  const [bulkProducts, setBulkProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [filteredBulkProducts, setFilteredBulkProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bulkLoading, setBulkLoading] = useState(true);
  const [error, setError] = useState('');
  const [bulkError, setBulkError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [bulkSearchTerm, setBulkSearchTerm] = useState('');

  useEffect(() => {
    fetchProducts();
    fetchBulkProducts();
  }, []);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredProducts(products);
    } else {
      const filtered = products.filter(product => 
        product.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.sku.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.vendor.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.price.includes(searchTerm)
      );
      setFilteredProducts(filtered);
    }
  }, [searchTerm, products]);

  useEffect(() => {
    if (bulkSearchTerm.trim() === '') {
      setFilteredBulkProducts(bulkProducts);
    } else {
      const filtered = bulkProducts.filter(product => 
        product.title.toLowerCase().includes(bulkSearchTerm.toLowerCase()) ||
        product.sku.toLowerCase().includes(bulkSearchTerm.toLowerCase()) ||
        product.vendor.toLowerCase().includes(bulkSearchTerm.toLowerCase()) ||
        product.price.includes(searchTerm)
      );
      setFilteredBulkProducts(filtered);
    }
  }, [bulkSearchTerm, bulkProducts]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await axios.get('https://gokulnair.com/new_project/api/products');
      setProducts(response.data);
      setFilteredProducts(response.data);
    } catch (err) {
      setError('Failed to fetch products');
      console.error('Error fetching products:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchBulkProducts = async () => {
    try {
      setBulkLoading(true);
      const response = await axios.get('https://gokulnair.com/rpo_bulk/api/products');
      setBulkProducts(response.data);
      setFilteredBulkProducts(response.data);
    } catch (err) {
      setBulkError('Failed to fetch bulk products');
      console.error('Error fetching bulk products:', err);
    } finally {
      setBulkLoading(false);
    }
  };

  const formatPrice = (price) => {
    return `$${parseFloat(price).toFixed(2)}`;
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const refreshProducts = () => {
    if (activeTab === 'weight-loss') {
      fetchProducts();
    } else {
      fetchBulkProducts();
    }
  };

  if (loading && bulkLoading) {
    return (
      <Layout>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading products...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="products-content">
        {/* Header Section */}
        <div className="products-header">
          <div className="header-left">
            <h1>Product Management</h1>
            <p>Manage and view all your products across different categories</p>
          </div>
          <button 
            className="refresh-button"
            onClick={refreshProducts}
            disabled={activeTab === 'weight-loss' ? loading : bulkLoading}
          >
            {activeTab === 'weight-loss' ? (loading ? '🔄' : '🔄') : (bulkLoading ? '🔄' : '🔄')} Refresh
          </button>
        </div>

        {/* Error Messages */}
        {error && (
          <div className="error-message">
            <div className="error-icon">❌</div>
            <div className="error-content">
              <h3>Error</h3>
              <p>{error}</p>
            </div>
          </div>
        )}

        {bulkError && activeTab === 'bulk' && (
          <div className="error-message">
            <div className="error-icon">❌</div>
            <div className="error-content">
              <h3>Error</h3>
              <p>{bulkError}</p>
            </div>
          </div>
        )}
        
        {/* Tabs */}
        <div className="products-tabs">
          <button
            className={`tab-button ${activeTab === 'weight-loss' ? 'active' : ''}`}
            onClick={() => handleTabChange('weight-loss')}
          >
            RPO Weight Loss
          </button>
          <button
            className={`tab-button ${activeTab === 'bulk' ? 'active' : ''}`}
            onClick={() => handleTabChange('bulk')}
          >
            RPO Bulk
          </button>
        </div>

        {/* RPO Weight Loss Tab Content */}
        {activeTab === 'weight-loss' && (
          <>
            {/* Stats Cards */}
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-icon">📦</div>
                <div className="stat-content">
                  <h3>Total Products</h3>
                  <p className="stat-number">{products.length.toLocaleString()}</p>
                </div>
              </div>
              
              <div className="stat-card">
                <div className="stat-icon">🔍</div>
                <div className="stat-content">
                  <h3>Filtered Results</h3>
                  <p className="stat-number">{filteredProducts.length.toLocaleString()}</p>
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-icon">💰</div>
                <div className="stat-content">
                  <h3>Average Price</h3>
                  <p className="stat-number">
                    {products.length > 0 
                      ? formatPrice(products.reduce((sum, p) => sum + parseFloat(p.price), 0) / products.length)
                      : '$0.00'
                    }
                  </p>
                </div>
              </div>
            </div>

            {/* Search Section */}
            <div className="search-section">
              <div className="search-container">
                <div className="search-icon">🔍</div>
                <input
                  type="text"
                  placeholder="Search RPO Weight Loss products by title, SKU, vendor, or price..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
                {searchTerm && (
                  <button 
                    onClick={() => setSearchTerm('')}
                    className="clear-search"
                  >
                    ✕
                  </button>
                )}
              </div>
              <div className="search-results">
                {searchTerm && (
                  <p>Found {filteredProducts.length} RPO Weight Loss product(s) matching "{searchTerm}"</p>
                )}
              </div>
            </div>

            {/* Products Grid */}
            <div className="products-section">
              <div className="section-header">
                <h2>RPO Weight Loss Products</h2>
                <span className="product-count">{filteredProducts.length} products</span>
              </div>
              
              {loading ? (
                <div className="loading-container">
                  <div className="loading-spinner"></div>
                  <p>Loading products...</p>
                </div>
              ) : (
                <div className="products-grid">
                  {filteredProducts.length === 0 ? (
                    <div className="no-products">
                      <div className="no-products-icon">📋</div>
                      <h3>No Products Found</h3>
                      <p>{searchTerm ? `No products found matching "${searchTerm}"` : 'No products available at the moment'}</p>
                    </div>
                  ) : (
                    filteredProducts.map((product) => (
                      <div key={product.id} className="product-card">
                        <div className="product-image">
                          <img 
                            src={product.image_src.replace(/\\/g, '')} 
                            alt={product.title}
                            onError={(e) => {
                              e.target.src = 'https://via.placeholder.com/300x200?text=No+Image';
                            }}
                          />
                          <div className="product-overlay">
                            <span className="product-category">Weight Loss</span>
                          </div>
                        </div>
                        <div className="product-info">
                          <h3 className="product-title">{product.title}</h3>
                          <div className="product-details">
                            <div className="detail-item">
                              <span className="detail-label">SKU:</span>
                              <span className="detail-value">{product.sku}</span>
                            </div>
                            <div className="detail-item">
                              <span className="detail-label">Vendor:</span>
                              <span className="detail-value">{product.vendor}</span>
                            </div>
                            <div className="detail-item">
                              <span className="detail-label">Price:</span>
                              <span className="detail-value price">{formatPrice(product.price)}</span>
                            </div>
                            <div className="detail-item">
                              <span className="detail-label">Created:</span>
                              <span className="detail-value">{new Date(product.created_at).toLocaleDateString()}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </>
        )}

        {/* RPO Bulk Tab Content */}
        {activeTab === 'bulk' && (
          <>
            {/* Stats Cards */}
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-icon">📦</div>
                <div className="stat-content">
                  <h3>Total Bulk Products</h3>
                  <p className="stat-number">{bulkProducts.length.toLocaleString()}</p>
                </div>
              </div>
              
              <div className="stat-card">
                <div className="stat-icon">🔍</div>
                <div className="stat-content">
                  <h3>Filtered Results</h3>
                  <p className="stat-number">{filteredBulkProducts.length.toLocaleString()}</p>
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-icon">💰</div>
                <div className="stat-content">
                  <h3>Average Price</h3>
                  <p className="stat-number">
                    {bulkProducts.length > 0 
                      ? formatPrice(bulkProducts.reduce((sum, p) => sum + parseFloat(p.price), 0) / bulkProducts.length)
                      : '$0.00'
                    }
                  </p>
                </div>
              </div>
            </div>

            {/* Search Section */}
            <div className="search-section">
              <div className="search-container">
                <div className="search-icon">🔍</div>
                <input
                  type="text"
                  placeholder="Search RPO Bulk products by title, SKU, vendor, or price..."
                  value={bulkSearchTerm}
                  onChange={(e) => setBulkSearchTerm(e.target.value)}
                  className="search-input"
                />
                {bulkSearchTerm && (
                  <button 
                    onClick={() => setBulkSearchTerm('')}
                    className="clear-search"
                  >
                    ✕
                  </button>
                )}
              </div>
              <div className="search-results">
                {bulkSearchTerm && (
                  <p>Found {filteredBulkProducts.length} RPO Bulk product(s) matching "{bulkSearchTerm}"</p>
                )}
              </div>
            </div>

            {/* Products Grid */}
            <div className="products-section">
              <div className="section-header">
                <h2>RPO Bulk Products</h2>
                <span className="product-count">{filteredBulkProducts.length} products</span>
              </div>
              
              {bulkLoading ? (
                <div className="loading-container">
                  <div className="loading-spinner"></div>
                  <p>Loading bulk products...</p>
                </div>
              ) : (
                <div className="products-grid">
                  {filteredBulkProducts.length === 0 ? (
                    <div className="no-products">
                      <div className="no-products-icon">📋</div>
                      <h3>No Bulk Products Found</h3>
                      <p>{bulkSearchTerm ? `No bulk products found matching "${bulkSearchTerm}"` : 'No bulk products available at the moment'}</p>
                    </div>
                  ) : (
                    filteredBulkProducts.map((product) => (
                      <div key={product.id} className="product-card">
                        <div className="product-image">
                          <img 
                            src={product.image_src.replace(/\\/g, '')} 
                            alt={product.title}
                            onError={(e) => {
                              e.target.src = 'https://via.placeholder.com/300x200?text=No+Image';
                            }}
                          />
                          <div className="product-overlay">
                            <span className="product-category">Bulk</span>
                          </div>
                        </div>
                        <div className="product-info">
                          <h3 className="product-title">{product.title}</h3>
                          <div className="product-details">
                            <div className="detail-item">
                              <span className="detail-label">SKU:</span>
                              <span className="detail-value">{product.sku}</span>
                            </div>
                            <div className="detail-item">
                              <span className="detail-label">Price:</span>
                              <span className="detail-value price">{formatPrice(product.price)}</span>
                            </div>
                            <div className="detail-item">
                              <span className="detail-label">Created:</span>
                              <span className="detail-value">{new Date(product.created_at).toLocaleDateString()}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </Layout>
  );
};

export default Products; 